/**
 * What the board shows when NOVA has no Azure credential.
 *
 * This is a configuration state, not an outage, and it is the expected state
 * for a fresh container: the backend image has no `az` CLI, so a developer's
 * `az login` on the host is invisible to it until either `~/.azure` is mounted
 * in or a real identity is assigned. Rather than an empty dashboard, the board
 * says exactly which of those to do.
 */

import { CloudCog, Icon } from "@norican/icons";
import { Alert, Card, Typography } from "@norican/react";

interface CloudSetupPanelProps {
  /** The backend's own explanation — rendered verbatim, never paraphrased. */
  statusDetail: string;
  /** How many subscriptions the board would monitor once it can reach Azure. */
  expectedSubscriptions: number;
}

export function CloudSetupPanel({ statusDetail, expectedSubscriptions }: CloudSetupPanelProps) {
  return (
    <Card variant="outline" accent="left" accentTone="warning">
      <Card.Header>
        <div className="flex items-center gap-3">
          <Icon icon={CloudCog} size="md" />
          <Typography variant="heading-4" as="h2">
            Azure is not connected
          </Typography>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          <Alert tone="warning" title="No Azure credential is available to the NOVA backend">
            {statusDetail}
          </Alert>

          <div className="flex flex-col gap-2">
            <Typography variant="body-sm" tone="muted">
              NOVA reads Azure with <code className="text-fg">DefaultAzureCredential</code> and
              needs no more than <strong className="text-fg">Reader</strong> on the{" "}
              {expectedSubscriptions} subscriptions it monitors. Any one of these fixes it:
            </Typography>
            <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
              <li>
                <span className="text-fg font-medium">In Azure</span> — assign a managed identity to
                the <code className="text-fg">nova-api</code> Container App and grant it Reader at
                the management-group or subscription scope.
              </li>
              <li>
                <span className="text-fg font-medium">Anywhere</span> — set{" "}
                <code className="text-fg">AZURE_TENANT_ID</code>,{" "}
                <code className="text-fg">AZURE_CLIENT_ID</code> and{" "}
                <code className="text-fg">AZURE_CLIENT_SECRET</code> for a service principal with
                Reader.
              </li>
              <li>
                <span className="text-fg font-medium">Locally</span> — run the backend natively (
                <code className="text-fg">uv run poe dev</code>) on a machine with an active{" "}
                <code className="text-fg">az login</code>. The backend container image ships no
                Azure CLI, so a host login is invisible to it; in Docker, use the service principal
                above.
              </li>
            </ul>
          </div>

          <Typography variant="body-sm" tone="muted">
            Every panel below stays empty until then. Nothing on this board is simulated, so an
            unconfigured backend shows nothing rather than something plausible.
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
}
